import dynamic, { type LoaderComponent } from 'next/dynamic';
import { type ComponentType, useEffect, useMemo } from 'react';

const DEFAULT_PREFETCH_DELAY_MS = 1500;

type LazySheetLoader<Props> = () => LoaderComponent<Props>;

interface UseLazySheetOptions {
  prefetch?: boolean;
  prefetchDelayMs?: number;
}

export const useLazySheet = <Props extends object>(
  loadSheet: LazySheetLoader<Props>,
  { prefetch = false, prefetchDelayMs = DEFAULT_PREFETCH_DELAY_MS }: UseLazySheetOptions = {},
): ComponentType<Props> => {
  const Sheet = useMemo(() => dynamic<Props>(loadSheet, { ssr: false }), [loadSheet]);

  useEffect(() => {
    if (!prefetch) return;

    const timeoutId = window.setTimeout(() => {
      void loadSheet().catch(error => {
        // Prefetch is an optimization. A stale or unavailable async chunk should not crash the page.
        // eslint-disable-next-line no-console
        console.warn('Failed to prefetch lazy sheet chunk', error);
      });
    }, prefetchDelayMs);

    return () => window.clearTimeout(timeoutId);
  }, [loadSheet, prefetch, prefetchDelayMs]);

  return Sheet;
};
